import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, Col, Dropdown, Menu, Modal, Row } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import { ModalProps } from '@/types/ModalProps';
import { Key } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { SorterResult } from 'antd/lib/table/interface';
import { tableSorterOrder } from '@/utils/table';
import InfiniteTable from '../InfiniteTable';
import FiltersItemsRequestPurchase from './FiltersItemsRequestPurchase';
import { useListRequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.hooks';
import {
  RequestPurchaseColumnConfig,
  RequestPurchaseFieldOption,
  RequestPurchaseItemTypeColor,
  RequestPurchaseItemTypeLabel,
  RequestPurchases,
  RequestPurchasesQuery,
} from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';

import { quantityFormatter } from '@/utils/number';
import { FcInfo } from 'react-icons/fc';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { useDebounce } from '@/hooks';
import StatusTag from '../StatusTag';

interface Props extends ModalProps {
  selectedRequestPurchases?: RequestPurchases[];
  finishCallback?: (insurers?: RequestPurchases[]) => void;
  loading: boolean;
  companyId: number;
  purchaseItemsRequestPurchaseIdsSelected?: number[];
}

const ItemsRequestPurchaseModal: React.FC<Props> = ({
  visible,
  handleVisible,
  selectedRequestPurchases = [],
  finishCallback,
  loading,
  companyId,
  purchaseItemsRequestPurchaseIdsSelected,
}: Props) => {
  const intl = useIntl();

  const [selectedRequestPurchaseKeys, setSelectedRequestPurchaseKeys] =
    useState<RequestPurchases[]>([]);

  const [queryParams, setQueryParams] = useState<RequestPurchasesQuery>({
    order: 'DESC',
    companyIdFilter: companyId,
  });

  const queryDebounce = useDebounce(queryParams);

  const { data, isFetching, hasNextPage, fetchNextPage, dataUpdatedAt } =
    useListRequestPurchases(queryDebounce, visible);

  useEffect(() => {
    setSelectedRequestPurchaseKeys([]);
  }, [dataUpdatedAt]);

  const requestPurchases = flatInfiniteData(data);

  const handleCancel = () => {
    handleVisible();
    setQueryParams({ order: 'DESC', companyIdFilter: companyId });
  };

  function handleOrderRequestPurchase(sorter: SorterResult<RequestPurchases>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderByPurchaseQuotation ===
            RequestPurchaseColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
              companyIdFilter: companyId,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderByPurchaseQuotation:
              RequestPurchaseColumnConfig[sorter.columnKey],
            companyIdFilter: companyId,
          };
        }

        return prev;
      });
    }
  }

  useEffect(() => {
    if (visible) {
      setSelectedRequestPurchaseKeys(selectedRequestPurchases);
    }
  }, [visible]);

  const handleRequestPurchasesSelect = (
    _: Key[],
    selectedRows: RequestPurchases[],
  ) => {
    setSelectedRequestPurchaseKeys(selectedRows);
  };

  const handleFinishCallback = () => {
    const requestPurchases = selectedRequestPurchaseKeys;

    if (finishCallback) {
      finishCallback(requestPurchases);
      setQueryParams({ order: 'DESC', companyIdFilter: companyId });
    } else {
      handleCancel();
    }
  };

  const columns: ColumnsType<RequestPurchases> = [
    {
      ellipsis: true,
      width: 140,
      dataIndex: 'workOrderNumber',
      key: RequestPurchaseFieldOption.workOrder,
      title: intl.formatMessage({
        id: 'commons.table.column.workOrder',
      }),
      sorter: true,
      render: (value) => value ?? '-',
    },
    {
      dataIndex: 'licensePlate',
      key: RequestPurchaseFieldOption.licensePlate,
      ellipsis: true,
      width: 140,
      title: intl.formatMessage({
        id: 'commons.table.column.licensePlate',
      }),
      sorter: true,
      render: (value) => value ?? '-',
    },
    useConfigurationColumnsPart({
      internalCode: ['item', 'sku'],
      manufacturingCode: ['item', 'manufacturingCode'],
      barcode: ['item', 'barCode'],
      companyCode: ['item', 'companyCode'],
      isRequestPurchase: true,
    }),
    {
      ellipsis: true,
      width: 140,
      dataIndex: 'type',
      title: 'Tipo',
      render: (_, item) => (
        <StatusTag
          label={RequestPurchaseItemTypeLabel[item.type]}
          color={RequestPurchaseItemTypeColor[item.type]}
        />
      ),
    },
    {
      ellipsis: true,
      dataIndex: 'description',
      key: RequestPurchaseFieldOption.description,
      title: intl.formatMessage({
        id: 'commons.table.column.description',
      }),
      width: 300,
      sorter: true,
    },
    {
      width: 40,
      render: (_value, row) => {
        return (
          <Dropdown
            overlay={
              <Menu mode="vertical" style={{ width: 250 }}>
                <Menu.Item key={1}>{`Total Solicitado: ${quantityFormatter(
                  row.requestedQuantity,
                )}`}</Menu.Item>
                <Menu.Item key={2}>{`Total em cotação: ${quantityFormatter(
                  row.quantityQuotation,
                )}`}</Menu.Item>
                <Menu.Item key={3}>{`Enviado para Compra: ${quantityFormatter(
                  row.quantitySentPurchased,
                )}`}</Menu.Item>
                <Menu.Item key={4}>{`Total comprado: ${quantityFormatter(
                  row.quantityPurchased,
                )}`}</Menu.Item>
                <Menu.Item key={5}>{`Total a comprar: ${quantityFormatter(
                  row.total,
                )}`}</Menu.Item>
              </Menu>
            }
            trigger={['click']}
            arrow
            placement="topCenter"
          >
            <FcInfo size={20} id="note-type-icon" cursor={'pointer'} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Modal
      title={intl.formatMessage({ id: 'stockCorrections.domain.items.add' })}
      open={visible}
      onCancel={handleCancel}
      width={1000}
      cancelText={intl.formatMessage({ id: 'commons.form.button.close' })}
      destroyOnClose
      footer={[
        <Button onClick={handleCancel}>Cancelar</Button>,
        <Button
          type="primary"
          loading={loading}
          onClick={handleFinishCallback}
          disabled={!selectedRequestPurchaseKeys.length}
        >
          Adicionar
        </Button>,
      ]}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <FiltersItemsRequestPurchase
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />

            <InfiniteTable
              rowSelection={{
                selectedRowKeys: selectedRequestPurchaseKeys.map(
                  (purchase) => purchase.id,
                ),
                preserveSelectedRowKeys: true,
                onChange: handleRequestPurchasesSelect,
                getCheckboxProps: (item) => ({
                  disabled: purchaseItemsRequestPurchaseIdsSelected?.includes(
                    item.workItemId,
                  ),
                }),
              }}
              columns={columns}
              onChange={(_p, _f, sorter) => {
                if (!Array.isArray(sorter)) {
                  handleOrderRequestPurchase(sorter);
                }
              }}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              dataSource={requestPurchases}
              loading={isFetching}
              className="scroll-table"
              rowKey={(row) => row.id}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default ItemsRequestPurchaseModal;
